import type {
  AgentStatusResponse,
  AddressPrefillResponse,
  AmendmentsResponse,
  Badge,
  BillCommitteesResponse,
  BillCosponsorsResponse,
  BillDetailResponse,
  BillVotesResponse,
  BillFacetsResponse,
  BillsListResponse,
  BillRelatedBillsResponse,
  BillSubjectsResponse,
  BillSummariesResponse,
  CalendarEventsResponse,
  CivicFeedContextResponse,
  CivicSettingsUpdateRequest,
  CivicSyncHistoryResponse,
  CivicSyncResponse,
  CivicSyncStatusResponse,
  CommitteeMeetingsResponse,
  CommitteeMeetingDetailResponse,
  CRSReportDetailResponse,
  CRSReportsResponse,
  HearingDetailResponse,
  DistrictContextResponse,
  HearingsResponse,
  NominationDetailResponse,
  NominationsResponse,
  ProfileResponse,
  RecentVotesResponse,
  RepresentativeProfileResponse,
  RepresentativesResponse,
  ResetQuestionnaireResponse,
  SettingsResponse,
  SubmitQuestionnaireRequest,
  SubmitQuestionnaireResponse,
  SystemInfo,
  QuestionnaireDefinitionResponse,
  QuestionnaireResponsesResponse,
  TreatiesResponse,
  UpdateProfileRequest,
  UserAddress,
  Entitlement,
  VotesResponse,
  VoteMembersResponse,
  LLMSettings,
  AnalysisStatus,
  BillAnalysis,
  BillUserAlignment,
  CTASummary,
  CTAListResponse,
  CTADetailResponse,
  FollowedTopicsResponse,
  NotificationsResponse,
  NotificationsSummary,
  TopicsRawResponse,
  TopicsResponse,
  CommitteesListResponse,
  CommitteeDetailResponse,
  CivicCommitteeMeeting,
  CivicHearing,
  CivicTreaty,
} from './types'

import { getToken, setToken, clearToken } from './auth'
import { isApiSessionCurrent, registerAccountRequest, resolveApiSession, StaleSessionError, type ApiSession } from './sessionFence'

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api'

// Called once by App when running in multi-user mode so that a 401 mid-session
// can flip the login gate without api.ts importing React.
let onUnauthorized: (() => void) | null = null
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message)
    this.name = 'ApiError'
  }
}

type ApiFetchInit = RequestInit & { session?: ApiSession | null; deadlineMs?: number; auth?: boolean }
const UNCERTAIN_MUTATION_DEADLINE_MS = 30_000

function requestWithDeadline(init: ApiFetchInit | undefined, controller: AbortController): { requestInit: RequestInit; clearDeadline: () => void } {
  const { session: _session, deadlineMs, auth: _auth, ...requestInit } = init ?? {}
  const externalSignal = requestInit.signal
  const abortFromCaller = () => controller.abort(externalSignal?.reason)
  externalSignal?.addEventListener('abort', abortFromCaller, { once: true })
  if (externalSignal?.aborted) abortFromCaller()
  const timeout = deadlineMs ?? (!requestInit.method || requestInit.method === 'GET' ? 15000 : null)
  if (timeout === null) return { requestInit: { ...requestInit, signal: controller.signal }, clearDeadline: () => externalSignal?.removeEventListener('abort', abortFromCaller) }
  const timer = window.setTimeout(() => controller.abort(), timeout)
  controller.signal.addEventListener('abort', () => window.clearTimeout(timer), { once: true })
  return { requestInit: { ...requestInit, signal: controller.signal }, clearDeadline: () => { window.clearTimeout(timer); externalSignal?.removeEventListener('abort', abortFromCaller) } }
}

async function apiFetch<T>(path: string, init?: ApiFetchInit): Promise<T> {
  const session = resolveApiSession(init?.session)
  const authEnabled = init?.auth !== false
  const token = authEnabled ? getToken() : null
  if (session && token !== session.expectedToken) throw new StaleSessionError()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> ?? {}),
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const controller = new AbortController()
  const unregister = session ? registerAccountRequest(controller) : () => {}
  let clearDeadline = () => {}
  try {
    const boundedRequest = requestWithDeadline(init, controller)
    const requestInit = boundedRequest.requestInit
    clearDeadline = boundedRequest.clearDeadline
    const response = await fetch(`${API_BASE}${path}`, { ...requestInit, headers })
    const sessionStillCurrent = !session || (isApiSessionCurrent(session) && getToken() === token)

    if (response.status === 401) {
      // This token comparison is mandatory even for boot/auth calls that do not
      // yet have a validated ApiSession. An old token's delayed 401 must never
      // clear a replacement token.
      const mayClear = getToken() === token && sessionStillCurrent
      if (authEnabled && mayClear) {
        clearToken()
        if (onUnauthorized) {
          try { onUnauthorized() } catch { /* don't let a handler error mask the API error */ }
        }
      }
    }

    if (!sessionStillCurrent) throw new StaleSessionError()

    if (!response.ok) {
      const text = await response.text()
      if (session && (!isApiSessionCurrent(session) || getToken() !== token)) throw new StaleSessionError()
      throw new ApiError(text || `API request failed: ${response.status}`, response.status)
    }

    // Keep the deadline and account-request registration alive through body
    // consumption. Headers alone do not complete a request.
    const text = await response.text()
    if (session && (!isApiSessionCurrent(session) || getToken() !== token)) throw new StaleSessionError()
    if (!text) return undefined as T
    return JSON.parse(text) as T
  } finally {
    clearDeadline()
    unregister()
  }
}

// ---------------------------------------------------------------------------
// JSON-field normalizers
//
// Some committee/meeting/hearing/treaty fields arrive as stringified JSON
// from the backend depending on the provider path. Views used to re-parse
// each field inline; now we parse once at the API boundary so the typed
// shape the views see is always `Array<...>` or `string[]`, never `| string`.
// ---------------------------------------------------------------------------

function normalizeJsonFields<T extends Record<string, unknown>>(
  obj: T,
  fields: readonly string[],
): T {
  let mutated: Record<string, unknown> | null = null
  for (const field of fields) {
    const raw = obj[field]
    if (typeof raw !== 'string') continue
    const trimmed = raw.trim()
    if (!trimmed || (trimmed[0] !== '[' && trimmed[0] !== '{')) continue
    try {
      const parsed = JSON.parse(trimmed)
      if (!mutated) mutated = { ...obj }
      mutated[field] = parsed
    } catch {
      /* leave the raw string alone */
    }
  }
  return (mutated as T | null) ?? obj
}

const MEETING_JSON_FIELDS = [
  'committee_codes', 'committee_names', 'committees',
  'related_bill_ids', 'documents', 'notes',
] as const

const HEARING_JSON_FIELDS = [
  'hearing_dates', 'committee_codes', 'committee_names', 'committees',
  'documents', 'witnesses', 'notes',
] as const

const TREATY_JSON_FIELDS = [
  'countries_parties', 'titles', 'related_docs', 'actions', 'committees',
] as const

function normalizeMeeting(m: CivicCommitteeMeeting): CivicCommitteeMeeting {
  return normalizeJsonFields(m as unknown as Record<string, unknown>, MEETING_JSON_FIELDS) as unknown as CivicCommitteeMeeting
}

function normalizeHearing(h: CivicHearing): CivicHearing {
  return normalizeJsonFields(h as unknown as Record<string, unknown>, HEARING_JSON_FIELDS) as unknown as CivicHearing
}

function normalizeTreaty(t: CivicTreaty): CivicTreaty {
  return normalizeJsonFields(t as unknown as Record<string, unknown>, TREATY_JSON_FIELDS) as unknown as CivicTreaty
}

function isDataObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function requireObjectRows(value: unknown, collection: string): Record<string, unknown>[] {
  if (!Array.isArray(value) || value.some((row) => !isDataObject(row))) {
    throw new Error(`Invalid ${collection} data. Please retry.`)
  }
  return value
}

function requireStringFields(
  row: Record<string, unknown>,
  required: readonly string[],
  nullable: readonly string[],
  label: string,
): void {
  if (required.some((field) => typeof row[field] !== 'string')
    || nullable.some((field) => row[field] != null && typeof row[field] !== 'string')) {
    throw new Error(`Invalid ${label} data. Please retry.`)
  }
}

function requireNullableStringFields(row: Record<string, unknown>, fields: readonly string[], label: string): void {
  if (fields.some((field) => !(field in row) || (row[field] !== null && typeof row[field] !== 'string'))) {
    throw new Error(`Invalid ${label} data. Please retry.`)
  }
}

const VOTE_MEMBER_REQUIRED_STRINGS = ['id', 'vote_id', 'member_id'] as const
const VOTE_MEMBER_NULLABLE_STRINGS = ['canonical_member_id', 'provider_person_id', 'full_name', 'vote_cast', 'vote_cast_normalized', 'party', 'state', 'district', 'synced_at', 'updated_at'] as const

function validateVoteMemberRow(row: Record<string, unknown>, label = 'vote member record'): void {
  requireStringFields(row, VOTE_MEMBER_REQUIRED_STRINGS, VOTE_MEMBER_NULLABLE_STRINGS, label)
}

function validateRepresentation(value: unknown): void {
  if (!isDataObject(value)) throw new Error('Invalid representative vote data. Please retry.')
  requireStringFields(value, [], ['status'], 'representative vote')
  for (const row of requireObjectRows(value.records, 'representative vote records')) {
    requireStringFields(
      row,
      ['member_id', 'member_name'],
      ['canonical_member_id', 'party', 'state', 'district', 'chamber', 'vote_record_status', 'vote_cast_normalized', 'vote_cast_raw'],
      'representative vote record',
    )
  }
}

// --- Auth endpoints ---

export interface AuthResponse {
  token: string
  refresh_token?: string
  user: {
    id: string
    email: string | null
    display_name: string
  }
  entitlement?: Entitlement | null
}

export interface AuthMeResponse {
  user: {
    id: string
    email: string | null
    display_name: string
    // True when this account can change/reset its Constituent password.
    // False for OAuth-only accounts (Google-managed). Older backends omit
    // the field; consumers treat undefined as true to preserve prior UX.
    has_password?: boolean
  }
  entitlement?: Entitlement | null
}

// When email verification is enabled, /auth/register withholds the token and
// returns this instead — the client must collect a code via /auth/verify-email.
export interface VerificationRequired {
  verification_required: true
  email: string
}

export type RegisterResult = AuthResponse | VerificationRequired

export function isVerificationRequired(r: RegisterResult): r is VerificationRequired {
  return 'verification_required' in r
}

export function authRegister(
  email: string,
  password: string,
  displayName: string = '',
): Promise<RegisterResult> {
  return apiFetch<RegisterResult>('/auth/register', {
    session: null,
    method: 'POST',
    body: JSON.stringify({ email, password, display_name: displayName }),
  })
}

export function authLogin(email: string, password: string): Promise<AuthResponse> {
  return apiFetch<AuthResponse>('/auth/login', {
    session: null,
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
}

export function authMe(signal?: AbortSignal, session?: ApiSession | null): Promise<AuthMeResponse> {
  return apiFetch<AuthMeResponse>('/auth/me', { signal, session })
}

export async function login(email: string, password: string): Promise<AuthResponse> {
  const result = await authLogin(email, password)
  setToken(result.token)
  return result
}

export async function register(
  email: string,
  password: string,
  displayName: string = '',
): Promise<RegisterResult> {
  const result = await authRegister(email, password, displayName)
  // Verification-disabled path returns a token straight away; the hard-gate
  // path returns { verification_required } and no token (set later by verifyEmail).
  if (!isVerificationRequired(result)) {
    setToken(result.token)
  }
  return result
}

// Confirm the emailed code; on success the server issues the real JWT.
export async function verifyEmail(email: string, code: string): Promise<AuthResponse> {
  const result = await apiFetch<AuthResponse>('/auth/verify-email', {
    session: null,
    method: 'POST',
    body: JSON.stringify({ email, code }),
  })
  setToken(result.token)
  return result
}

// Re-send a verification code. Always resolves (enumeration-safe on the server).
export async function resendCode(email: string): Promise<void> {
  await apiFetch<{ ok: boolean }>('/auth/resend-code', {
    session: null,
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

export function authOAuth(
  provider: 'google',
  oauthToken: string,
  nonce: string,
  displayName: string = '',
): Promise<AuthResponse> {
  return apiFetch<AuthResponse>('/auth/oauth', {
    session: null,
    method: 'POST',
    body: JSON.stringify({
      oauth_provider: provider,
      oauth_token: oauthToken,
      nonce,
      display_name: displayName,
    }),
  })
}

export async function loginWithGoogle(oauthToken: string, nonce: string): Promise<AuthResponse> {
  const result = await authOAuth('google', oauthToken, nonce)
  setToken(result.token)
  return result
}

const APPLE_WEB_TRANSACTION_PREFIX = 'constituent.apple.web.'

function base64Url(bytes: Uint8Array): string {
  let binary = ''
  bytes.forEach((byte) => { binary += String.fromCharCode(byte) })
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function appleVerifier(): string {
  const bytes = new Uint8Array(32)
  globalThis.crypto.getRandomValues(bytes)
  return base64Url(bytes)
}

async function appleVerifierChallenge(verifier: string): Promise<string> {
  if (!globalThis.crypto?.subtle) throw new Error('Apple sign-in requires a secure browser context.')
  const bytes = new TextEncoder().encode(verifier)
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes)
  return base64Url(new Uint8Array(digest))
}

interface AppleStartResponse {
  transaction: string
  authorization_url: string
  expires_in: number
}

function appleTransactionKey(transaction: string): string {
  return `${APPLE_WEB_TRANSACTION_PREFIX}${transaction}`
}

export async function startAppleWebSignIn(createNewAccount = false): Promise<void> {
  const verifier = appleVerifier()
  const verifierChallenge = await appleVerifierChallenge(verifier)
  const started = await apiFetch<AppleStartResponse>('/auth/apple/start', {
    session: null,
    auth: false,
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ verifier_challenge: verifierChallenge }),
  })
  sessionStorage.setItem(
    appleTransactionKey(started.transaction),
    JSON.stringify({ verifier, createNewAccount, expiresAt: Date.now() + Math.max(1, started.expires_in) * 1000 }),
  )
  window.location.assign(started.authorization_url)
}

export async function completeAppleWebSignIn(transaction: string): Promise<AuthResponse> {
  const key = appleTransactionKey(transaction)
  const raw = sessionStorage.getItem(key)
  if (!raw) throw new Error('Apple sign-in expired. Please start again from the sign-in page.')
  let verifier = ''
  let createNewAccount = false
  let expiresAt = 0
  try {
    const parsed = JSON.parse(raw) as { verifier?: unknown; createNewAccount?: unknown; expiresAt?: unknown }
    createNewAccount = parsed.createNewAccount === true
    verifier = typeof parsed.verifier === 'string' ? parsed.verifier : ''
    expiresAt = typeof parsed.expiresAt === 'number' ? parsed.expiresAt : 0
  } catch {
    sessionStorage.removeItem(key)
    throw new Error('Apple sign-in expired. Please start again from the sign-in page.')
  }
  if (!verifier || expiresAt <= Date.now()) {
    sessionStorage.removeItem(key)
    throw new Error('Apple sign-in expired. Please start again from the sign-in page.')
  }
  try {
    const result = await apiFetch<AuthResponse>('/auth/apple/complete', {
      session: null,
      auth: false,
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ transaction, verifier, create_new_account: createNewAccount }),
    })
    setToken(result.token)
    return result
  } finally {
    sessionStorage.removeItem(key)
  }
}

// Server-side logout hook. No state to revoke today (JWTs are stateless); this
// is the call site that refresh-token rotation lands behind in TODO-1. The
// catch is intentional — we always want to clear local state even if the
// backend is unreachable.
export async function authLogout(session?: ApiSession): Promise<void> {
  try {
    await apiFetch<void>('/auth/logout', { method: 'POST', session })
  } catch (error) {
    if (error instanceof StaleSessionError) throw error
    /* ignore — clearing the local token is the load-bearing step */
  }
}

export async function logout(session?: ApiSession): Promise<void> {
  await authLogout(session)
  clearToken()
  window.location.reload()
}

export async function deleteAccount(session?: ApiSession): Promise<void> {
  await apiFetch<void>('/account', { method: 'DELETE', session })
}

// ---------------------------------------------------------------------------
// Password reset (anonymous) and password change (authenticated) endpoints.
// All four are 404 in single-user mode; reset endpoints are 404 when SMTP /
// email verification is disabled (LoginView guards with system-info flag).
// ---------------------------------------------------------------------------

export function requestPasswordReset(email: string): Promise<void> {
  return apiFetch<{ ok: boolean }>('/auth/password-reset/request', {
    session: null,
    method: 'POST',
    body: JSON.stringify({ email }),
  }).then(() => undefined)
}

export function verifyPasswordResetCode(
  email: string,
  code: string,
): Promise<{ reset_token: string }> {
  return apiFetch<{ reset_token: string }>('/auth/password-reset/verify', {
    session: null,
    method: 'POST',
    body: JSON.stringify({ email, code }),
  })
}

export function completePasswordReset(
  resetToken: string,
  newPassword: string,
): Promise<void> {
  return apiFetch<void>('/auth/password-reset/complete', {
    session: null,
    method: 'POST',
    body: JSON.stringify({ reset_token: resetToken, new_password: newPassword }),
  })
}

export function changePassword(
  currentPassword: string,
  newPassword: string,
  session?: ApiSession,
): Promise<void> {
  return apiFetch<void>('/auth/password/change', {
    method: 'POST',
    session,
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  })
}

export function getSystemInfo(signal?: AbortSignal): Promise<SystemInfo> {
  return apiFetch<SystemInfo>('/system/info', { signal, session: null })
}

export function getAgentStatus(): Promise<AgentStatusResponse> {
  return apiFetch<AgentStatusResponse>('/agent/status')
}

export function startAgent(): Promise<{ agent: AgentStatusResponse['agent'] }> {
  return apiFetch('/agent/start', { method: 'POST' })
}

export function stopAgent(): Promise<{ agent: AgentStatusResponse['agent'] }> {
  return apiFetch('/agent/stop', { method: 'POST' })
}

export function enqueueTask(name: string, payload: Record<string, unknown>): Promise<{ task: unknown }> {
  return apiFetch('/tasks', {
    method: 'POST',
    body: JSON.stringify({ name, payload }),
  })
}

export function getSettings(signal?: AbortSignal, session?: ApiSession): Promise<SettingsResponse> {
  return apiFetch<SettingsResponse>('/settings', { signal, session })
}

export function saveAddress(address: UserAddress, session?: ApiSession): Promise<SettingsResponse> {
  return apiFetch<SettingsResponse>('/settings/address', {
    method: 'POST',
    session,
    body: JSON.stringify(address),
  })
}

// Read-only GPS prefill: converts browser coordinates into an address
// candidate without writing anything. Coordinates stay in the POST body;
// the 15s deadline bounds the separate browser-to-openVoter leg.
export function prefillAddressByCoordinates(lat: number, lng: number, session?: ApiSession, signal?: AbortSignal): Promise<AddressPrefillResponse> {
  return apiFetch<AddressPrefillResponse>('/settings/address/prefill', {
    method: 'POST',
    session,
    signal,
    deadlineMs: 15000,
    body: JSON.stringify({ lat, lng }),
  })
}

export function resolveDistricts(): Promise<SettingsResponse> {
  return apiFetch<SettingsResponse>('/settings/resolve-districts', {
    method: 'POST',
  })
}

export function saveCivicSettings(payload: CivicSettingsUpdateRequest, session?: ApiSession): Promise<SettingsResponse> {
  return apiFetch<SettingsResponse>('/settings/civic', {
    method: 'POST',
    session,
    body: JSON.stringify(payload),
  })
}

export function getProfile(): Promise<ProfileResponse> {
  return apiFetch<ProfileResponse>('/profile')
}

export function updateProfile(payload: UpdateProfileRequest, session?: ApiSession): Promise<{ ok: boolean; profile: ProfileResponse['profile'] }> {
  return apiFetch('/profile', {
    method: 'PATCH',
    session,
    body: JSON.stringify(payload),
  })
}

export function getQuestionnaireDefinition(signal?: AbortSignal, session?: ApiSession): Promise<QuestionnaireDefinitionResponse> {
  return apiFetch<QuestionnaireDefinitionResponse>('/questionnaire', { signal, session })
}

export function getQuestionnaireResponses(signal?: AbortSignal, session?: ApiSession): Promise<QuestionnaireResponsesResponse> {
  return apiFetch<QuestionnaireResponsesResponse>('/questionnaire/responses', { signal, session })
}

export function submitQuestionnaire(payload: SubmitQuestionnaireRequest, session?: ApiSession, signal?: AbortSignal): Promise<SubmitQuestionnaireResponse> {
  return apiFetch<SubmitQuestionnaireResponse>('/questionnaire/submit', {
    method: 'POST',
    session,
    signal,
    deadlineMs: 30000,
    body: JSON.stringify(payload),
  })
}

export function resetQuestionnaire(): Promise<ResetQuestionnaireResponse> {
  return apiFetch<ResetQuestionnaireResponse>('/questionnaire/reset', {
    method: 'POST',
  })
}

export function getDistrictContext(signal?: AbortSignal, session?: ApiSession): Promise<DistrictContextResponse> {
  return apiFetch<DistrictContextResponse>('/context/districts', { signal, session })
}

export function syncCivic(trigger: 'manual' | 'auto' = 'manual', session?: ApiSession): Promise<CivicSyncResponse> {
  const normalized = trigger === 'auto' ? 'auto' : 'manual'
  return apiFetch<CivicSyncResponse>(`/sync/civic?trigger=${normalized}`, {
    method: 'POST',
    session,
  })
}

export function getCivicFeedContext(signal?: AbortSignal, session?: ApiSession): Promise<CivicFeedContextResponse> {
  return apiFetch<CivicFeedContextResponse>('/context/civic-feed', { signal, session })
}

export function getCivicSyncStatus(signal?: AbortSignal): Promise<CivicSyncStatusResponse> {
  return apiFetch<CivicSyncStatusResponse>('/sync/civic/status', { signal })
}

export function getLLMSettings(signal?: AbortSignal): Promise<LLMSettings> {
  return apiFetch<LLMSettings>('/settings/llm', { signal })
}

export function saveLLMSettings(payload: { provider: string; endpoint_url: string; model: string; api_key: string }): Promise<LLMSettings> {
  return apiFetch<LLMSettings>('/settings/llm', {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
}

export function testLLMConnection(): Promise<{ success: boolean; message: string }> {
  return apiFetch<{ success: boolean; message: string }>('/settings/llm/test', { method: 'POST' })
}

export function getAnalysisStatus(signal?: AbortSignal): Promise<{ analysis: AnalysisStatus }> {
  return apiFetch<{ analysis: AnalysisStatus }>('/analysis/status', { signal })
}

export function getCivicSyncHistory(limit = 20, signal?: AbortSignal): Promise<CivicSyncHistoryResponse> {
  return apiFetch<CivicSyncHistoryResponse>(`/sync/civic/history?limit=${Math.max(1, Math.min(limit, 200))}`, { signal })
}

export function getBillDetail(billId: string, signal?: AbortSignal): Promise<BillDetailResponse> {
  return apiFetch<BillDetailResponse>(`/bills/${encodeURIComponent(billId)}/detail`, { signal })
}

export function saveBill(billId: string, session?: ApiSession): Promise<void> {
  return apiFetch<void>(`/bills/${encodeURIComponent(billId)}/save`, { method: 'POST', session, deadlineMs: UNCERTAIN_MUTATION_DEADLINE_MS })
}

export function unsaveBill(billId: string, session?: ApiSession): Promise<void> {
  return apiFetch<void>(`/bills/${encodeURIComponent(billId)}/save`, { method: 'DELETE', session, deadlineMs: UNCERTAIN_MUTATION_DEADLINE_MS })
}

export function saveRepresentative(memberId: string, session?: ApiSession): Promise<void> {
  return apiFetch<void>(`/representatives/${encodeURIComponent(memberId)}/save`, { method: 'POST', session, deadlineMs: UNCERTAIN_MUTATION_DEADLINE_MS })
}

export function unsaveRepresentative(memberId: string, session?: ApiSession): Promise<void> {
  return apiFetch<void>(`/representatives/${encodeURIComponent(memberId)}/save`, { method: 'DELETE', session, deadlineMs: UNCERTAIN_MUTATION_DEADLINE_MS })
}

export function getBills(params?: {
  level?: string
  query?: string
  outcome?: 'in_progress' | 'stalled' | 'passed' | 'failed'
  matter_types?: string[]
  alignment?: string
  sort?: string
  order?: 'asc' | 'desc'
  limit?: number
  offset?: number
  saved?: boolean
}, signal?: AbortSignal, session?: ApiSession): Promise<BillsListResponse> {
  // /api/bills is implicitly enriched-only now — the backend drops the
  // query param because unenriched bills must never surface through the API.
  const query = new URLSearchParams()
  if (params?.level) query.set('level', params.level)
  if (params?.query) query.set('query', params.query)
  if (params?.outcome) query.set('outcome', params.outcome)
  if (params?.matter_types && params.matter_types.length > 0) {
    for (const mt of params.matter_types) {
      query.append('matter_types', mt)
    }
  }
  if (params?.alignment) query.set('alignment', params.alignment)
  if (params?.sort) query.set('sort', params.sort)
  if (params?.order) query.set('order', params.order)
  if (params?.limit) query.set('limit', String(Math.max(1, Math.min(params.limit, 200))))
  if (params?.offset) query.set('offset', String(Math.max(0, params.offset)))
  if (params?.saved) query.set('saved', 'true')
  const qs = query.toString()
  return apiFetch<BillsListResponse>(`/bills${qs ? `?${qs}` : ''}`, { signal, session })
}

export function getBillFacets(params?: { level?: string }): Promise<BillFacetsResponse> {
  const query = new URLSearchParams()
  if (params?.level) query.set('level', params.level)
  const qs = query.toString()
  return apiFetch<BillFacetsResponse>(`/bills/facets${qs ? `?${qs}` : ''}`)
}

export function getRepresentatives(params?: {
  query?: string
  level?: string
  state?: string
  chamber?: string
  mineOnly?: boolean
  limit?: number
  saved?: boolean
}, signal?: AbortSignal, session?: ApiSession): Promise<RepresentativesResponse> {
  const query = new URLSearchParams()
  if (params?.query) query.set('query', params.query)
  if (params?.level) query.set('level', params.level)
  if (params?.state) query.set('state', params.state)
  if (params?.chamber) query.set('chamber', params.chamber)
  if (params?.mineOnly) query.set('mine_only', 'true')
  if (params?.limit) query.set('limit', String(Math.max(1, Math.min(params.limit, 2500))))
  if (params?.saved) query.set('saved', 'true')
  const qs = query.toString()
  return apiFetch<RepresentativesResponse>(`/representatives${qs ? `?${qs}` : ''}`, { signal, session })
}

export async function getRepresentativeProfile(memberId: string, signal?: AbortSignal, session?: ApiSession): Promise<RepresentativeProfileResponse> {
  const data: unknown = await apiFetch(`/representatives/${encodeURIComponent(memberId)}`, { cache: 'no-store', signal, session })
  if (!isDataObject(data) || !isDataObject(data.profile) || !isDataObject(data.profile.member)) {
    throw new Error('Invalid representative profile data. Please retry.')
  }
  requireStringFields(
    data.profile.member,
    ['id', 'level', 'provider', 'provider_id', 'name'],
    ['party', 'state', 'district', 'chamber', 'office', 'website', 'phone', 'email', 'contact_form', 'address', 'image_url', 'federal_office_group', 'synced_at', 'updated_at'],
    'representative profile member',
  )
  if (typeof data.profile.member.active !== 'boolean') {
    throw new Error('Invalid representative profile member data. Please retry.')
  }
  for (const committee of requireObjectRows(data.profile.committees, 'representative committees')) {
    requireStringFields(committee, ['id', 'level', 'provider', 'provider_id', 'name'], ['congress', 'chamber', 'committee_code', 'website', 'role', 'synced_at', 'updated_at'], 'representative committee')
  }
  for (const vote of requireObjectRows(data.profile.recent_votes, 'representative recent_votes')) {
    validateVoteMemberRow(vote, 'representative recent vote')
    requireStringFields(vote, [], ['question', 'result', 'vote_date', 'chamber', 'roll_call_status', 'vote_record_status', 'vote_cast_raw', 'related_bill_id', 'bill_number', 'bill_title'], 'representative recent vote')
  }
  for (const collection of ['sponsored_bills', 'cosponsored_bills'] as const) {
    for (const bill of requireObjectRows(data.profile[collection], `representative ${collection}`)) {
      requireStringFields(bill, ['id', 'level', 'provider', 'provider_id', 'title'], ['sponsor', 'type', 'priority', 'member_link_role', 'member_link_source', 'cosponsor_withdrawn_date'], 'representative bill')
      requireNullableStringFields(bill, ['status', 'last_action', 'date'], 'representative bill')
    }
  }
  if (!isDataObject(data.profile.vote_summary)
    || Object.values(data.profile.vote_summary).some((count) => !Number.isInteger(count) || (count as number) < 0)) {
    throw new Error('Invalid representative vote summary data. Please retry.')
  }
  return data as unknown as RepresentativeProfileResponse
}

const RECENT_VOTE_NULLABLE_STRINGS = ['question', 'result', 'vote_date', 'chamber', 'roll_call_status', 'vote_record_status', 'vote_cast_raw', 'related_bill_id', 'bill_number', 'bill_title'] as const

/**
 * One grouped recent-votes request for the given current representatives.
 * `undefined` targets every current representative (bare mode); an empty
 * array resolves locally without a request; nonempty arrays use repeated
 * `member_id` query parameters in the given order.
 */
export async function getRecentVotes(memberIds?: string[], signal?: AbortSignal, session?: ApiSession): Promise<RecentVotesResponse> {
  if (memberIds !== undefined && memberIds.length === 0) {
    return { representatives: [] }
  }
  const query = new URLSearchParams()
  for (const id of memberIds ?? []) {
    // Every raw element stays one repeated parameter (duplicates and blanks
    // included) so the endpoint's raw-count cap and blank rejection stay
    // server-enforced; each value is trimmed to the endpoint's own semantics.
    query.append('member_id', String(id).trim())
  }
  const qs = query.toString()
  const data: unknown = await apiFetch(`/recent-votes${qs ? `?${qs}` : ''}`, { cache: 'no-store', signal, session })
  if (!isDataObject(data) || !Array.isArray(data.representatives)) {
    throw new Error('Invalid recent votes data. Please retry.')
  }
  for (const group of requireObjectRows(data.representatives, 'recent votes groups')) {
    if (!isDataObject(group.member) || !Array.isArray(group.recent_votes)) {
      throw new Error('Invalid recent votes data. Please retry.')
    }
    requireStringFields(group.member, ['id', 'level', 'provider', 'provider_id', 'name'], [], 'recent votes member')
    if (typeof group.member.active !== 'boolean') {
      throw new Error('Invalid recent votes member data. Please retry.')
    }
    for (const vote of requireObjectRows(group.recent_votes, 'recent votes')) {
      validateVoteMemberRow(vote, 'recent vote')
      requireStringFields(vote, [], RECENT_VOTE_NULLABLE_STRINGS, 'recent vote')
      // The service guarantees each vote belongs to its group.
      if (vote.member_id !== group.member.id || vote.canonical_member_id !== group.member.id) {
        throw new Error('Invalid recent votes data. Please retry.')
      }
    }
  }
  if (memberIds !== undefined) {
    // Returned groups must be an ordered subset of the trimmed,
    // deduplicated request — the same subset semantics the endpoint applies.
    const requested = [...new Set(memberIds.map((id) => String(id).trim()))]
    const returned = (data.representatives as Array<{ member: { id: unknown } }>)
      .map((group) => group.member.id)
    let cursor = 0
    for (const id of returned) {
      const found = requested.indexOf(id as string, cursor)
      if (found === -1) {
        throw new Error('Invalid recent votes data. Please retry.')
      }
      cursor = found + 1
    }
  }
  return data as unknown as RecentVotesResponse
}

export function getCalendarEvents(params?: {
  start_date?: string
  end_date?: string
  event_type?: string
  member_id?: string
  bill_id?: string
  provider?: string
  level?: string
  include_past?: boolean
  sort?: 'asc' | 'desc'
  offset?: number
  limit?: number
}): Promise<CalendarEventsResponse> {
  const query = new URLSearchParams()
  if (params?.start_date) query.set('start_date', params.start_date)
  if (params?.end_date) query.set('end_date', params.end_date)
  if (params?.event_type) query.set('event_type', params.event_type)
  if (params?.member_id) query.set('member_id', params.member_id)
  if (params?.bill_id) query.set('bill_id', params.bill_id)
  if (params?.provider) query.set('provider', params.provider)
  if (params?.level) query.set('level', params.level)
  if (params?.include_past !== undefined) query.set('include_past', params.include_past ? 'true' : 'false')
  if (params?.sort) query.set('sort', params.sort)
  if (params?.offset !== undefined) query.set('offset', String(Math.max(0, params.offset)))
  if (params?.limit) query.set('limit', String(Math.max(1, Math.min(params.limit, 2000))))
  const qs = query.toString()
  return apiFetch<CalendarEventsResponse>(`/calendar/events${qs ? `?${qs}` : ''}`)
}

// -- Tier 1: Bill enrichment endpoints --

export function getBillCosponsors(billId: string, limit = 500): Promise<BillCosponsorsResponse> {
  const qs = limit !== 500 ? `?limit=${Math.max(1, Math.min(limit, 1000))}` : ''
  return apiFetch<BillCosponsorsResponse>(`/bills/${encodeURIComponent(billId)}/cosponsors${qs}`)
}

export function getBillSubjects(billId: string): Promise<BillSubjectsResponse> {
  return apiFetch<BillSubjectsResponse>(`/bills/${encodeURIComponent(billId)}/subjects`)
}

export function getBillSummaries(billId: string): Promise<BillSummariesResponse> {
  return apiFetch<BillSummariesResponse>(`/bills/${encodeURIComponent(billId)}/summaries`)
}

export function getBillRelatedBills(billId: string): Promise<BillRelatedBillsResponse> {
  return apiFetch<BillRelatedBillsResponse>(`/bills/${encodeURIComponent(billId)}/related-bills`)
}

export function getBillCommittees(billId: string): Promise<BillCommitteesResponse> {
  return apiFetch<BillCommitteesResponse>(`/bills/${encodeURIComponent(billId)}/committees`)
}

// -- Standalone list endpoints for amendments and votes --

export function getAmendments(params?: {
  bill_id?: string
  limit?: number
}): Promise<AmendmentsResponse> {
  const query = new URLSearchParams()
  if (params?.bill_id) query.set('bill_id', params.bill_id)
  if (params?.limit) query.set('limit', String(Math.max(1, Math.min(params.limit, 2500))))
  const qs = query.toString()
  return apiFetch<AmendmentsResponse>(`/amendments${qs ? `?${qs}` : ''}`)
}

export function getVotes(params?: {
  limit?: number
  offset?: number
  sort_by?: 'vote_date' | 'updated_at'
  chamber?: 'house' | 'senate' | 'council' | 'committee'
  provider?: string
}): Promise<VotesResponse> {
  const query = new URLSearchParams()
  if (params?.limit) query.set('limit', String(Math.max(1, Math.min(params.limit, 2500))))
  if (params?.offset) query.set('offset', String(Math.max(0, params.offset)))
  if (params?.sort_by) query.set('sort_by', params.sort_by)
  if (params?.chamber) query.set('chamber', params.chamber)
  if (params?.provider) query.set('provider', params.provider)
  const qs = query.toString()
  return apiFetch<VotesResponse>(`/votes${qs ? `?${qs}` : ''}`)
}

export async function getBillVotes(billId: string, signal?: AbortSignal): Promise<BillVotesResponse> {
  const data: unknown = await apiFetch(`/bills/${encodeURIComponent(billId)}/votes`, { cache: 'no-store', signal })
  if (!isDataObject(data)) throw new Error('Invalid bill votes data. Please retry.')
  const votes = requireObjectRows(data.votes, 'bill votes')
  if (!Number.isInteger(data.total) || data.total !== votes.length) {
    throw new Error('Invalid bill vote total. Please retry.')
  }
  for (const vote of votes) {
    requireStringFields(
      vote,
      ['id', 'level', 'provider', 'provider_id'],
      ['chamber', 'congress', 'session', 'vote_number', 'question', 'result', 'vote_date', 'related_bill_id', 'related_amendment_id', 'synced_at', 'updated_at', 'vote_at', 'roll_call_status'],
      'bill vote',
    )
    const representation = vote.user_representation
    if (representation == null) continue
    validateRepresentation(representation)
  }
  return data as unknown as BillVotesResponse
}

export function getBillAnalysis(billId: string): Promise<{ analysis: BillAnalysis | null }> {
  return apiFetch<{ analysis: BillAnalysis | null }>(`/bills/${encodeURIComponent(billId)}/analysis`).catch(() => ({ analysis: null }))
}

export function getBillAlignment(billId: string): Promise<{ alignment: BillUserAlignment | null }> {
  return apiFetch<{ alignment: BillUserAlignment | null }>(`/bills/${encodeURIComponent(billId)}/alignment`).catch(() => ({ alignment: null }))
}

export async function getVoteMembers(voteId: string, signal?: AbortSignal): Promise<VoteMembersResponse> {
  const data: unknown = await apiFetch(`/votes/${encodeURIComponent(voteId)}/members`, { cache: 'no-store', signal })
  if (!isDataObject(data)) throw new Error('Invalid vote members data. Please retry.')
  requireStringFields(data, ['vote_id'], ['roll_call_status'], 'vote members')
  for (const row of requireObjectRows(data.records, 'vote member records')) validateVoteMemberRow(row)
  return data as unknown as VoteMembersResponse
}

// -- Tier 2: New entity endpoints --

export function getNominations(params?: {
  congress?: string
  query?: string
  status?: string
  limit?: number
  offset?: number
}): Promise<NominationsResponse> {
  const query = new URLSearchParams()
  if (params?.congress) query.set('congress', params.congress)
  if (params?.query) query.set('query', params.query)
  if (params?.status) query.set('status', params.status)
  if (params?.limit) query.set('limit', String(Math.max(1, Math.min(params.limit, 2500))))
  if (params?.offset !== undefined) query.set('offset', String(Math.max(0, params.offset)))
  const qs = query.toString()
  return apiFetch<NominationsResponse>(`/nominations${qs ? `?${qs}` : ''}`)
}

export function getNominationDetail(nominationId: string): Promise<NominationDetailResponse> {
  return apiFetch<NominationDetailResponse>(`/nominations/${encodeURIComponent(nominationId)}`)
}

export function getCommittees(params?: {
  level?: string
  chamber?: string
  query?: string
  limit?: number
}): Promise<CommitteesListResponse> {
  const q = new URLSearchParams()
  if (params?.level) q.set('level', params.level)
  if (params?.chamber) q.set('chamber', params.chamber)
  if (params?.query) q.set('query', params.query)
  if (params?.limit) q.set('limit', String(params.limit))
  const qs = q.toString()
  return apiFetch<CommitteesListResponse>(`/committees${qs ? `?${qs}` : ''}`)
}

export function getCommitteeDetail(committeeId: string): Promise<CommitteeDetailResponse> {
  return apiFetch<CommitteeDetailResponse>(`/committees/${encodeURIComponent(committeeId)}`).then((res) => ({
    ...res,
    meetings: (res.meetings ?? []).map(normalizeMeeting),
    hearings: (res.hearings ?? []).map(normalizeHearing),
  }))
}

export function getCommitteeMeetings(params?: {
  congress?: string
  limit?: number
}): Promise<CommitteeMeetingsResponse> {
  const query = new URLSearchParams()
  if (params?.congress) query.set('congress', params.congress)
  if (params?.limit) query.set('limit', String(Math.max(1, Math.min(params.limit, 2500))))
  const qs = query.toString()
  return apiFetch<CommitteeMeetingsResponse>(`/committee-meetings${qs ? `?${qs}` : ''}`).then((res) => ({
    ...res,
    meetings: (res.meetings ?? []).map(normalizeMeeting),
  }))
}

export function getCommitteeMeetingDetail(meetingId: string): Promise<CommitteeMeetingDetailResponse> {
  return apiFetch<CommitteeMeetingDetailResponse>(`/committee-meetings/${encodeURIComponent(meetingId)}`).then((res) => ({
    ...res,
    meeting: normalizeMeeting(res.meeting),
  }))
}

export function getHearings(params?: {
  congress?: string
  limit?: number
}): Promise<HearingsResponse> {
  const query = new URLSearchParams()
  if (params?.congress) query.set('congress', params.congress)
  if (params?.limit) query.set('limit', String(Math.max(1, Math.min(params.limit, 2500))))
  const qs = query.toString()
  return apiFetch<HearingsResponse>(`/hearings${qs ? `?${qs}` : ''}`).then((res) => ({
    ...res,
    hearings: (res.hearings ?? []).map(normalizeHearing),
  }))
}

export function getHearingDetail(hearingId: string): Promise<HearingDetailResponse> {
  return apiFetch<HearingDetailResponse>(`/hearings/${encodeURIComponent(hearingId)}`).then((res) => ({
    ...res,
    hearing: normalizeHearing(res.hearing),
  }))
}

export function getTreaties(params?: {
  congress?: string
  limit?: number
}): Promise<TreatiesResponse> {
  const query = new URLSearchParams()
  if (params?.congress) query.set('congress', params.congress)
  if (params?.limit) query.set('limit', String(Math.max(1, Math.min(params.limit, 2500))))
  const qs = query.toString()
  return apiFetch<TreatiesResponse>(`/treaties${qs ? `?${qs}` : ''}`).then((res) => ({
    ...res,
    treaties: (res.treaties ?? []).map(normalizeTreaty),
  }))
}

export function getCRSReports(params?: {
  offset?: number
  limit?: number
}): Promise<CRSReportsResponse> {
  const query = new URLSearchParams()
  if (params?.offset !== undefined) query.set('offset', String(Math.max(0, params.offset)))
  if (params?.limit) query.set('limit', String(Math.max(1, Math.min(params.limit, 2500))))
  const qs = query.toString()
  return apiFetch<CRSReportsResponse>(`/crs-reports${qs ? `?${qs}` : ''}`)
}

export function getCRSReportDetail(reportId: string): Promise<CRSReportDetailResponse> {
  return apiFetch<CRSReportDetailResponse>(`/crs-reports/${encodeURIComponent(reportId)}`)
}

// -- Call to Action (CTA) API --

export function getCTASummary(signal?: AbortSignal, session?: ApiSession): Promise<CTASummary> {
  return apiFetch<CTASummary>('/ctas/summary', { signal, session })
}

export function getCTAs(params?: {
  status?: string
  alignment_type?: string
  limit?: number
  offset?: number
}, signal?: AbortSignal, session?: ApiSession): Promise<CTAListResponse> {
  const query = new URLSearchParams()
  if (params?.status) query.set('status', params.status)
  if (params?.alignment_type) query.set('alignment_type', params.alignment_type)
  if (params?.limit) query.set('limit', String(params.limit))
  if (params?.offset !== undefined) query.set('offset', String(params.offset))
  const qs = query.toString()
  return apiFetch<CTAListResponse>(`/ctas${qs ? `?${qs}` : ''}`, { signal, session })
}

export function getCTA(ctaId: string, signal?: AbortSignal, session?: ApiSession): Promise<CTADetailResponse> {
  return apiFetch<CTADetailResponse>(`/ctas/${encodeURIComponent(ctaId)}`, { signal, session })
}

export function updateCTAStatus(ctaId: string, status: 'viewed' | 'acted' | 'dismissed', session?: ApiSession): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(`/ctas/${encodeURIComponent(ctaId)}/status`, {
    method: 'PATCH',
    session,
    deadlineMs: UNCERTAIN_MUTATION_DEADLINE_MS,
    body: JSON.stringify({ status }),
  })
}

// Queues the same worker-side regrade a questionnaire submit triggers
// (publish-only; the CTA review worker reconciles, wipes, and rescores).
export function recomputeCTAs(): Promise<{ ok: boolean; queued: boolean }> {
  return apiFetch<{ ok: boolean; queued: boolean }>('/ctas/recompute', { method: 'POST' })
}

export function getBadges(): Promise<Badge[]> {
  return apiFetch<{ badges: Badge[] }>('/badges').then((res) => res.badges)
}

// -- Topic Notifications API --

export function getTopics(): Promise<TopicsResponse> {
  return apiFetch<TopicsRawResponse>('/topics').then((res) => ({
    themes: Object.entries(res.by_theme).map(([theme, topics]) => ({ theme, topics })),
  }))
}

export function getFollowedTopics(): Promise<FollowedTopicsResponse> {
  return apiFetch<FollowedTopicsResponse>('/topics/followed')
}

export function followTopic(key: string, session?: ApiSession): Promise<{ ok: boolean; followed: boolean }> {
  return apiFetch<{ ok: boolean; followed: boolean }>(`/topics/${encodeURIComponent(key)}/follow`, { method: 'POST', session, deadlineMs: UNCERTAIN_MUTATION_DEADLINE_MS })
}

export function unfollowTopic(key: string, session?: ApiSession): Promise<{ ok: boolean; followed: boolean }> {
  return apiFetch<{ ok: boolean; followed: boolean }>(`/topics/${encodeURIComponent(key)}/follow`, { method: 'DELETE', session, deadlineMs: UNCERTAIN_MUTATION_DEADLINE_MS })
}

export function followTopics(keys: string[], session?: ApiSession): Promise<{ ok: boolean; followed: string[]; limit_reached: boolean; limit: number }> {
  return apiFetch<{ ok: boolean; followed: string[]; limit_reached: boolean; limit: number }>('/topics/follow', {
    method: 'POST',
    session,
    deadlineMs: UNCERTAIN_MUTATION_DEADLINE_MS,
    body: JSON.stringify({ keys }),
  })
}

export function unfollowTopics(keys: string[], session?: ApiSession): Promise<{ ok: boolean; removed: number }> {
  return apiFetch<{ ok: boolean; removed: number }>('/topics/unfollow', {
    method: 'POST',
    session,
    deadlineMs: UNCERTAIN_MUTATION_DEADLINE_MS,
    body: JSON.stringify({ keys }),
  })
}

export function getNotifications(params?: {
  unread_only?: boolean
  limit?: number
  offset?: number
}, signal?: AbortSignal, session?: ApiSession): Promise<NotificationsResponse> {
  const query = new URLSearchParams()
  if (params?.unread_only) query.set('unread_only', 'true')
  if (params?.limit) query.set('limit', String(Math.max(1, Math.min(params.limit, 100))))
  if (params?.offset !== undefined) query.set('offset', String(Math.max(0, params.offset)))
  const qs = query.toString()
  return apiFetch<NotificationsResponse>(`/notifications${qs ? `?${qs}` : ''}`, { signal, session })
}

export function getNotificationsSummary(signal?: AbortSignal, session?: ApiSession): Promise<NotificationsSummary> {
  return apiFetch<NotificationsSummary>('/notifications/summary', { signal, session })
}

export function markNotificationsRead(ids?: string[], session?: ApiSession): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>('/notifications/read', {
    method: 'POST',
    session,
    deadlineMs: UNCERTAIN_MUTATION_DEADLINE_MS,
    body: JSON.stringify({ ids: ids ?? null }),
  })
}
